// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    IComputeResourceResponse,
    IShortComputeResourceResponse,
} from 'common/api/resources/ComputeResource';
import { initialComputeResourceState } from 'admin/computeResource/reducers';
import {
    IVmResponse,
    IShortVmResponse,
} from 'common/api/resources/ComputeResourceVm';
import { ITaskResponse } from 'common/api/resources/Task';
import {
    IPaginated,
    IRequest,
} from 'common/api/resources/Request/request';
import { IPaginateApiResponse } from 'common/api/resources/Response';

export interface IComputeResourceVmForDisasterRecovery {
    computeResourceVm: IVmResponse;
    requirements: IRequireResources;
}

export interface IDestinationComputeResource {
    compute_resource: IComputeResourceResponse;
    compatibility_details: {
        arch_type_is_compatible: boolean;
        compatible_destination_cr_ip_blocks_count: number;
        destination_cr_is_active: boolean;
        is_compatible: boolean;
        products_is_compatible: boolean;
        source_cr_ip_blocks_count: number;
        sources_is_compatible: boolean;
        storages_is_compatible: boolean;
        supported_virtualization_types: {
            kvm?: boolean;
            vz?: boolean;
        };
        missed_ip_blocks: Array<{
            id: number;
            name: string;
        }>;
        incompatible_storages: Array<{
            type: string;
            tier: string;
        }>;
    };
}

export interface IComputeResourceRecoveryReadinessStatusResponse {
    available_resources: {
        is_disk_unlimited: boolean;
        is_ram_unlimited: boolean;
        is_vcpu_unlimited: boolean;
        disk: number;
        ram: number;
        vcpu: number;
    };
    backup_coverage: {
        all: IBackupCoverage;
        with_disaster_recovery_enabled: IBackupCoverage;
    };
    destination_compute_resources: IDestinationComputeResource[];
    require_resources: {
        for_vms_disaster_recovery_enabled: IRequireResources;
        for_vms_with_backups: IRequireResources;
        for_all_vms: IRequireResources;
    };
    ready_for_recovery: boolean;
    lastUpdatedTime?: string;
}

interface IBackupCoverage {
    available_backups: number;
    expired_backups: number;
    total: number;
    without_backups: number;
}

interface IRequireResources {
    vcpu: number;
    ram: number;
    disk: number;
}
export interface IResilienceStatusTableComputeResource extends IComputeResourceResponse {
    ready_for_recovery: boolean;
}
export interface IResilienceStatusTableProps {
    compute_resources: IResilienceStatusTableComputeResource[];
    lastUpdatedTime?: string;
}

export const defaultComputeResourceReadinessStatus: IComputeResourceRecoveryReadinessStatusResponse = {
    available_resources: {
        is_disk_unlimited: true,
        is_ram_unlimited: true,
        is_vcpu_unlimited: true,
        disk: 0,
        ram: 0,
        vcpu: 0,
    },
    backup_coverage: {
        all: {
            available_backups: 1,
            expired_backups: 0,
            total: 1,
            without_backups: 0,
        },
        with_disaster_recovery_enabled: {
            available_backups: 1,
            expired_backups: 0,
            total: 1,
            without_backups: 0,
        },
    },
    destination_compute_resources: Array.from(Array(10), ((_, x) => ({
        compute_resource: {
            ...initialComputeResourceState,
            id: ++x,
            host: `testCRHost${x}`,
            name: `testCRName${x}`,
        },
        compatibility_details: {
            arch_type_is_compatible: true,
            compatible_destination_cr_ip_blocks_count: 1,
            destination_cr_is_active: true,
            is_compatible: x > 5,
            products_is_compatible: true,
            source_cr_ip_blocks_count: 1,
            sources_is_compatible: true,
            storages_is_compatible: true,
            supported_virtualization_types: {
                kvm: true,
                vz: true,
            },
            missed_ip_blocks: [
                {
                    id: 1,
                    name: 'test_ip_block',
                },
                {
                    id: 2,
                    name: 'test_ip_block',
                },
                {
                    id: 3,
                    name: 'test_ip_block',
                },
                {
                    id: 4,
                    name: 'test_ip_block',
                },
            ],
            incompatible_storages: [],
        },
    }))),
    require_resources: {
        for_all_vms: {
            vcpu: 2,
            ram: 429496,
            disk: 10,
        },
        for_vms_with_backups: {
            vcpu: 2,
            ram: 429496,
            disk: 10,
        },
        for_vms_disaster_recovery_enabled: {
            vcpu: 2123,
            ram: 100000000,
            disk: 100000000,
        },
    },
    ready_for_recovery: true,
};

export interface IDisasterRecoveryResponse {
    id: number;
    status: DisasterRecoveryStatus;
    source_compute_resource: IShortComputeResourceResponse;
    started_at: string;
    finished_at: string;
    is_loading?: boolean;
    entities?: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>;
    is_deleting_original_data?: boolean;
    entities_count: {
        total: number;
        running_entities_count: number;
    };
}

export interface IDisasterRecoveryEntityResponse {
    id: number;
    status: DisasterRecoveryEntityStatus;
    target_compute_resource?: IShortComputeResourceResponse;
    compute_resource_vm: IShortVmResponse;
    disaster_recovery: IDisasterRecoveryResponse;
    task?: ITaskResponse|null;
    started_at: string;
    finished_at: string;
    is_original_data_deleted: boolean;
    original_data: string;
    is_migrated_back: boolean;
    is_deleting_original_data?: boolean;
    fail_reason: string|null;
}

export interface IDisasterRecoveryOriginalDataResponse {
    id: number;
    original_data: string;
    compute_resource_vm: IShortComputeResourceResponse;
}

export enum DisasterRecoveryStatus {
    PENDING = 'pending',
    RUNNING = 'running',
    RUNNING_WITH_ERRORS = 'running_with_errors',
    FAILED = 'failed',
    COMPLETED = 'completed',
    COMPLETED_WITH_ERRORS = 'completed_with_errors',
}

export enum DisasterRecoveryEntityStatus {
    PENDING = 'pending',
    RUNNING = 'running',
    FAILED = 'failed',
    COMPLETED = 'completed',
}

export enum DisasterRecoveryEntitiesRequestFields {
    ID = 'id',
    COMPUTE_RESOURCE_VM = 'compute_resource_vm_id',
}

export interface IDisasterRecoveryHistoryRequestFilters {}

export interface IDisasterRecoveryHistoryRequest extends IRequest<
    IDisasterRecoveryHistoryRequestFilters
>, IPaginated {}

export interface IDisasterRecoveryEntitiesRequestFilters {
    disaster_recovery_id: number[];
    disaster_recovery_entity_id: number[];
}

export interface IDisasterRecoveryEntitiesRequest extends IRequest<
    IDisasterRecoveryEntitiesRequestFilters
>, IPaginated {}

export interface IDisasterRecoveryDeleteOriginalDataRequest {
    disaster_recovery_ids?: number[];
    disaster_recovery_entity_ids?: number[];
    is_deleted_manually: boolean;
}

export interface IDisasterRecoveryDownloadOriginalDataRequest {
    disaster_recovery_ids?: number[];
    disaster_recovery_entity_ids?: number[];
}

export enum TargetSelectionPolicy {
    ANY = 'any',
    SPECIFIED = 'specified',
}

export interface IStartDisasterRecoveryRequest {
    compute_resource_vm_ids: number[];
    target_selection_policy: TargetSelectionPolicy;
    target_compute_resource_ids: number[];
    send_notification: boolean;
    notification?: string|null;
}

export interface IUpdateDisasterRecoveryEntity {
    entity: IDisasterRecoveryEntityResponse;
}
